import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { UserAvatar } from '@/components/UserAvatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { format } from 'date-fns';
import { 
  UserCheck, 
  Trash, 
  Trash2, 
  Send, 
  Zap, 
  Loader2, 
  MoreVertical, 
  Edit, 
  User, 
  Mail, 
  Phone, 
  Building, 
  Calendar, 
  MapPin, 
  Users, 
  Plus, 
  Camera, 
  MessageSquare,
  CheckSquare,
  FileCheck,
  Megaphone,
  TrendingUp,
  DollarSign,
  FileText,
  ChevronDown,
  ChevronUp,
  Settings,
  Share2,
  Lightbulb,
  Target,
  Globe,
  PieChart,
  Maximize2,
  ChevronLeft,
  ChevronRight,
  Download
} from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { DEFAULT_AI_MODEL, DEFAULT_AI_PROVIDER } from '@/config/aiDefaults';
import { Badge } from '@/components/ui/badge';
import ReactMarkdown from 'react-markdown';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Link } from 'wouter';
import { ScreenshotCapture } from '@/components/ScreenshotCapture';

// Define types for our chat related data (copied from SmartAssistant)
interface ChatSession {
  id: number;
  userId: number;
  provider: string;
  model: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

interface ChatMessage {
  id: number;
  sessionId: number;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  metadata?: any;
}

interface ChatProvider {
  provider: string;
  name: string;
  description: string;
  models: Array<{
    id: string;
    name: string;
    description: string;
    maxTokens: number;
    available: boolean;
  }>;
  available: boolean;
}


// Message formatting functions (copied from SmartAssistant)
function getOrchestratorStatusBadge(metadata?: any) {
  if (!metadata) return null;
  
  const badges = [];
  
  // Add model information if available
  if (metadata.model) {
    badges.push(
      <Badge key="model" className="bg-indigo-600 text-white font-medium px-2 py-1 mr-1">
        {metadata.model}
      </Badge>
    );
  }
  
  // Add provider information if available
  if (metadata.provider) {
    badges.push(
      <Badge key="provider" className="bg-violet-600 text-white font-medium px-2 py-1 mr-1">
        {metadata.provider}
      </Badge>
    );
  }
  
  // Add orchestrator tool information if available
  if (metadata.orchestrator) {
    const { tool, confidence } = metadata.orchestrator;
    
    let color = 'bg-gray-300 text-gray-700';
    
    switch (tool) {
      case 'Pinecone_RAG':
        color = 'bg-purple-600 text-white';
        break;
      case 'General_LLM':
        color = 'bg-orange-600 text-white';
        break;
      case 'Web_Search':
        color = 'bg-green-600 text-white';
        break;
      case 'Error':
        color = 'bg-red-600 text-white';
        break;
    }
    
    // Format confidence as percentage
    const confidencePercent = Math.round(confidence * 100);
    
    badges.push(
      <Badge key="orchestrator" className={`${color} hover:${color} font-medium px-2 py-1`}>
        {tool} ({confidencePercent}%)
      </Badge>
    );
  }
  
  // Only render if we have badges
  if (badges.length === 0) return null;
  
  return (
    <div className="flex flex-wrap items-center gap-1 mb-2">
      {badges}
    </div>
  );
}

// Code block renderer for markdown
const CodeBlock = ({ language, value }: { language: string, value: string }) => {
  return (
    <div className="rounded-md my-4 overflow-hidden">
      <SyntaxHighlighter 
        language={language || 'javascript'} 
        style={vscDarkPlus}
        className="!bg-zinc-900"
        customStyle={{
          margin: 0,
          padding: '1rem',
          borderRadius: '0.375rem',
        }}
      >
        {value}
      </SyntaxHighlighter>
    </div>
  );
};

// Custom markdown components
const MarkdownComponents = {
  code({ node, inline, className, children, ...props }: any) {
    const match = /language-(\w+)/.exec(className || '');
    return !inline && match ? (
      <CodeBlock language={match[1]} value={String(children).replace(/\n$/, '')} />
    ) : (
      <code className="bg-zinc-800 text-pink-200 px-1.5 py-0.5 rounded-md font-mono text-sm" {...props}>
        {children}
      </code>
    );
  },
  p: ({ children }: any) => {
    // Process BBCode-style tags if present
    if (typeof children === 'string') {
      let content = children;
      let hasBBCode = false;
      
      // Process [b]bold[/b] tags
      const boldPattern = /\[b\](.*?)\[\/b\]/g;
      if (boldPattern.test(content)) {
        hasBBCode = true;
        content = content.replace(boldPattern, '<strong>$1</strong>');
      }
      
      // Process [i]italic[/i] tags
      const italicPattern = /\[i\](.*?)\[\/i\]/g;
      if (italicPattern.test(content)) {
        hasBBCode = true;
        content = content.replace(italicPattern, '<em>$1</em>');
      }
      
      // Process [u]underline[/u] tags
      const underlinePattern = /\[u\](.*?)\[\/u\]/g;
      if (underlinePattern.test(content)) {
        hasBBCode = true;
        content = content.replace(underlinePattern, '<u>$1</u>');
      }
      
      // Process [url=http://example.com]link text[/url] tags
      const urlPattern = /\[url=(.*?)\](.*?)\[\/url\]/g;
      if (urlPattern.test(content)) {
        hasBBCode = true;
        content = content.replace(urlPattern, '<a href="$1" class="text-orange-600 hover:underline" target="_blank" rel="noopener noreferrer">$2</a>');
      }
      
      // Process email addresses
      const emailPattern = /\b([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\b/g;
      if (emailPattern.test(content)) {
        hasBBCode = true;
        content = content.replace(emailPattern, '<a href="mailto:$1" class="text-orange-600 hover:underline">$1</a>');
      }
      
      // If we found and processed any BBCode, return the HTML
      if (hasBBCode) {
        return <p className="mb-4" dangerouslySetInnerHTML={{ __html: content }} />;
      }
    }
    
    // If no BBCode was found, return as normal
    return <p className="mb-4">{children}</p>;
  },
  a: ({ href, children, ...props }: any) => (
    <a 
      href={href} 
      className="text-orange-600 hover:underline" 
      target="_blank" 
      rel="noopener noreferrer" 
      {...props}
    >
      {children}
    </a>
  ),
  strong: ({ children }: any) => (
    <strong className="font-semibold">{children}</strong>
  ),
  em: ({ children }: any) => (
    <em className="italic">{children}</em>
  ),
};

// Render a separate Unsplash grid from structured metadata
function UnsplashGrid({ meta }: { meta: any }) {
  const images = meta?.unsplash?.images || [];
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  if (!images || images.length === 0) return null;

  const openAt = (i: number) => { setIndex(i); setOpen(true); };
  const close = () => setOpen(false);
  const next = () => setIndex((i) => Math.min(i + 1, images.length - 1));
  const prev = () => setIndex((i) => Math.max(i - 1, 0));
  const current = images[index] || {};

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, index]);

  const downloadImage = () => {
    try {
      const url = current.full_url || current.download_url || current.thumbnail_url;
      if (!url) return;

      const a = document.createElement('a');
      a.href = url;
      const urlObj = new URL(url);
      let base = urlObj.pathname.split('/').pop() || (current.id || 'image');
      if (!/\.(jpg|jpeg|png|webp)$/i.test(base)) base += '.jpg';
      a.download = base;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) { console.error('Download failed:', e); }
  };

  // Copy button removed

  return (
    <div className="mt-3">
      <div className="text-sm text-gray-600 mb-1">
        Visual Inspiration · {meta.unsplash.count} images for "{meta.unsplash.searchTerms}"
      </div>
      <div className="unsplash-image-grid">
        {images.map((img: any, i: number) => (
          <div key={img.id} className="unsplash-image-item" onClick={() => openAt(i)} title={img.description}>
            <img src={img.thumbnail_url} alt={img.description} className="unsplash-thumbnail" />
            <div className="unsplash-attribution">
              <a href={img.photographer.profile_url} target="_blank" rel="noopener noreferrer" onClick={(e)=>e.stopPropagation()}>{img.photographer.name}</a> | <a href="https://unsplash.com" target="_blank" rel="noopener noreferrer" onClick={(e)=>e.stopPropagation()}>Unsplash</a>
            </div>
            {/* meta hidden by CSS */}
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-[1000] bg-black/70 flex items-center justify-center" onClick={close}>
          <div
            className="relative max-w-[90vw] p-2 bg-white rounded-lg"
            style={{ minHeight: '300px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={close}
              className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white/90 text-gray-800 hover:bg-white shadow-lg border border-gray-200 flex items-center justify-center transition-colors"
              title="Close"
            >
              ✕
            </button>
            <div className="flex-1 w-full flex items-center justify-center overflow-hidden">
              <img src={current.full_url} alt={current.description} style={{ maxWidth: '85vw', maxHeight: '100%', height: 'auto', width: 'auto', objectFit: 'contain' }} />
            </div>
            <div className="flex items-center justify-between w-full px-2 py-2">
              <div className="text-xs text-gray-600">{index + 1} / {images.length}</div>
              <div className="flex items-center gap-2">
                <button onClick={prev} disabled={index === 0} className="p-2 bg-gray-100 rounded disabled:opacity-50" title="Previous">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button onClick={next} disabled={index === images.length - 1} className="p-2 bg-gray-100 rounded disabled:opacity-50" title="Next">
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button onClick={downloadImage} className="p-2 bg-gray-100 rounded" title="Download">
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function splitByUnsplashGrid(content: string): Array<{ type: 'text' | 'html'; content: string; key: string }>{
  const parts: Array<{ type: 'text' | 'html'; content: string; key: string }> = [];
  let cursor = 0;
  let key = 0;
  while (true) {
    const markerIdx = content.indexOf('unsplash-image-grid', cursor);
    if (markerIdx === -1) {
      const tail = content.slice(cursor);
      if (tail) parts.push({ type: 'text', content: tail, key: `text-${key++}` });
      break;
    }
    // Find the start of the surrounding <div ...>
    const startDiv = content.lastIndexOf('<div', markerIdx);
    if (startDiv === -1) {
      // Fallback: treat rest as text
      const tail = content.slice(cursor);
      if (tail) parts.push({ type: 'text', content: tail, key: `text-${key++}` });
      break;
    }
    // Push text before grid
    const before = content.slice(cursor, startDiv);
    if (before) parts.push({ type: 'text', content: before, key: `text-${key++}` });

    // Walk to find matching closing div for the grid block
    const re = /<\/?div\b/gi;
    re.lastIndex = startDiv;
    let depth = 0;
    let endIdx = -1;
    let m: RegExpExecArray | null;
    while ((m = re.exec(content)) !== null) {
      if (content[m.index + 1] === '/') depth--; else depth++;
      if (depth === 0 && m.index > startDiv) { endIdx = re.lastIndex; break; }
    }
    if (endIdx === -1) {
      // Could not find end; treat rest as text
      const tail = content.slice(startDiv);
      parts.push({ type: 'text', content: tail, key: `text-${key++}` });
      break;
    }
    const gridHtml = content.slice(startDiv, endIdx);
    parts.push({ type: 'html', content: gridHtml, key: `html-${key++}` });
    cursor = endIdx;
  }
  return parts;
}

const processMessageContent = (content: string) => {
  return splitByUnsplashGrid(content);
};

// Component to render processed message parts
const MessageContent: React.FC<{ content: string }> = ({ content }) => {
  const parts = processMessageContent(content);
  
  return (
    <div>
      {parts.map((part) => {
        if (part.type === 'html') {
          return (
            <div key={part.key} className="my-4" dangerouslySetInnerHTML={{ __html: part.content }} />
          );
        }
        // Render text content with markdown
        return (
          <div key={part.key} className="prose max-w-none text-foreground">
            <ReactMarkdown components={MarkdownComponents as any}>
              {(() => {
                // Process BBCode tags inline
                if (!part.content) return '';
                
                let processedContent = part.content;
                
                // Process [b]bold[/b] tags to markdown **bold**
                processedContent = processedContent.replace(/\[b\](.*?)\[\/b\]/g, '**$1**');
                
                // Process [i]italic[/i] tags to markdown *italic*
                processedContent = processedContent.replace(/\[i\](.*?)\[\/i\]/g, '*$1*');
                
                // Process [u]underline[/u] tags
                processedContent = processedContent.replace(/\[u\](.*?)\[\/u\]/g, '<u>$1</u>');
                
                // Process [url=http://example.com]link text[/url] tags to markdown
                processedContent = processedContent.replace(/\[url=(.*?)\](.*?)\[\/url\]/g, '[$2]($1)');
                
                // Process standalone email addresses to markdown links
                processedContent = processedContent.replace(/\b([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\b/g, '[$1](mailto:$1)');

                // Convert standalone URLs to markdown links
                processedContent = processedContent.replace(/(?<!\]\(|\))(https?:\/\/[^\s<>]+)(?![^\(]*\))/g, '[$1]($1)');

                // Format and link US phone numbers
                const phoneRegex = /(?<!\w)(?:\+1[\s.-]?)?(?:\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}(?!\w)/g;
                processedContent = processedContent.replace(phoneRegex, (m) => {
                  const digits = (m.match(/\d/g) || []).join('');
                  let d = digits;
                  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
                  if (d.length !== 10) return m;
                  const formatted = `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
                  const tel = digits.startsWith('1') ? digits : `1${d}`;
                  return `[${formatted}](tel:${tel})`;
                });
                
                return processedContent;
              })()}
            </ReactMarkdown>
          </div>
        );
      })}
    </div>
  );
};

export default function Workspace() {
  // Chat state (copied from SmartAssistant)
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const [selectedSession, setSelectedSession] = useState<number | null>(null);
  const [messageInput, setMessageInput] = useState('');
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState<number | null>(null);
  const [editSessionTitle, setEditSessionTitle] = useState('');
  const [showScreenshotCapture, setShowScreenshotCapture] = useState(false);

  // Workspace-specific state
  const [selectedDialog, setSelectedDialog] = useState<string | null>(null);
  const [todoCollapsed, setTodoCollapsed] = useState(false);
  const [approvalCollapsed, setApprovalCollapsed] = useState(false);
  const [maximizedCard, setMaximizedCard] = useState<string | null>(null);
  
  // Specialized AI assistant states
  const [specializedInput, setSpecializedInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [conversationHistory, setConversationHistory] = useState<Array<{role: string, content: string}>>([]);

  // Mock data for workspace cards
  const todoItems = [
    { 
      id: 1, 
      title: "Follow up with Johnson family", 
      description: "Contact the Johnson family regarding their Italy vacation preferences and provide updated package options",
      priority: "high", 
      dueDate: "Today",
      assignedTo: "Sarah Miller"
    },
    { 
      id: 2, 
      title: "Review Europe itinerary draft", 
      description: "Complete review of 14-day Europe tour itinerary for the Martinez group including logistics and accommodations",
      priority: "medium", 
      dueDate: "Tomorrow",
      assignedTo: "Travel Planning Team"
    },
    { 
      id: 3, 
      title: "Update client preferences", 
      description: "Input dietary restrictions and accessibility requirements for upcoming client bookings",
      priority: "low", 
      dueDate: "This week",
      assignedTo: "Client Services"
    },
    { 
      id: 4, 
      title: "Prepare presentation for Smith group", 
      description: "Create comprehensive presentation for corporate retreat proposal including venue options and activities",
      priority: "high", 
      dueDate: "Today",
      assignedTo: "Corporate Sales"
    },
    { 
      id: 5, 
      title: "Book restaurant reservations for Anderson trip", 
      description: "Secure dining reservations at recommended restaurants in Paris and Rome for anniversary celebration",
      priority: "medium", 
      dueDate: "Tomorrow",
      assignedTo: "Concierge Services"
    },
    { 
      id: 6, 
      title: "Research new cruise destinations", 
      description: "Investigate emerging cruise routes in Southeast Asia and evaluate partnership opportunities",
      priority: "low", 
      dueDate: "Next week",
      assignedTo: "Product Development"
    },
    { 
      id: 7, 
      title: "Update travel insurance information", 
      description: "Review and update travel insurance policies and coverage options for all destination packages",
      priority: "medium", 
      dueDate: "Friday",
      assignedTo: "Risk Management"
    },
  ];

  const approvalItems = [
    { 
      id: 1, 
      title: "Send marketing email to 250 prospects", 
      description: "Quarterly newsletter featuring new European river cruise packages and early bird pricing",
      type: "marketing",
      submittedDate: "Dec 14",
      submittedBy: "Marketing Team",
      amount: null
    },
    { 
      id: 2, 
      title: "Update pricing for luxury packages", 
      description: "Adjust pricing for premium accommodations in response to supplier rate changes",
      type: "sales",
      submittedDate: "Dec 13",
      submittedBy: "Sales Manager",
      amount: null
    },
    { 
      id: 3, 
      title: "Generate monthly revenue report", 
      description: "Comprehensive financial analysis including booking trends and commission tracking",
      type: "finance",
      submittedDate: "Dec 12",
      submittedBy: "Finance Team",
      amount: null
    },
    { 
      id: 4, 
      title: "Launch social media campaign for summer deals", 
      description: "Multi-platform campaign promoting Mediterranean cruise packages with limited-time offers",
      type: "marketing",
      submittedDate: "Dec 15",
      submittedBy: "Social Media Manager",
      amount: "$2,500"
    },
    { 
      id: 5, 
      title: "Approve vendor contract for ground transportation", 
      description: "Annual contract renewal with preferred ground transportation provider in Italy",
      type: "finance",
      submittedDate: "Dec 10",
      submittedBy: "Operations Manager",
      amount: "$15,000"
    },
    { 
      id: 6, 
      title: "Send follow-up emails to recent inquiries", 
      description: "Personalized follow-up sequence for leads generated from December trade show",
      type: "sales",
      submittedDate: "Dec 16",
      submittedBy: "Lead Development",
      amount: null
    },
  ];

  const quickActions = [
    { title: "New Contact", icon: Users, action: () => {} },
    { title: "Schedule Meeting", icon: Calendar, action: () => {} },
    { title: "Send Email", icon: Mail, action: () => {} },
    { title: "Create Document", icon: FileText, action: () => {} },
    { title: "Make Call", icon: Phone, action: () => {} },
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high": return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      case "medium": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
      case "low": return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      default: return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
    }
  };

  // Queries (copied from SmartAssistant)
  const { data: sessions, isLoading: isLoadingSessions } = useQuery({
    queryKey: ['/api/smart-chat/sessions'],
    queryFn: async () => {
      try {
        return await apiRequest('GET', '/api/smart-chat/sessions?userId=2');
      } catch (error) {
        console.error('Error fetching sessions:', error);
        throw error;
      }
    }
  });

  const { data: sessionData, isLoading: isLoadingMessages } = useQuery({
    queryKey: ['/api/smart-chat/sessions', selectedSession],
    queryFn: async () => {
      if (!selectedSession) return null;
      return await apiRequest('GET', `/api/smart-chat/sessions/${selectedSession}`);
    },
    enabled: !!selectedSession
  });

  const { data: providers } = useQuery({
    queryKey: ['/api/chat/providers'],
    staleTime: 1000 * 60 * 5, // 5 minutes
  });


  // Mutations (copied from SmartAssistant)
  const createSessionMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest('POST', '/api/smart-chat/sessions', {
        userId: 2,
        provider: DEFAULT_AI_PROVIDER,
        model: DEFAULT_AI_MODEL,
      });
    },
    onSuccess: (data) => {
      // Immediately update the sessions list with the new session
      const currentSessions = queryClient.getQueryData<ChatSession[]>(['/api/smart-chat/sessions']) || [];
      queryClient.setQueryData(
        ['/api/smart-chat/sessions'],
        [data, ...currentSessions]
      );
      
      // Initialize the session data in the cache so we don't have pending state
      queryClient.setQueryData(
        ['/api/smart-chat/sessions', data.id],
        { session: data, messages: [] }
      );
      
      // Set the selected session to the new one
      setSelectedSession(data.id);
      
      // Check if there's a pending message to send
      const pendingMessage = localStorage.getItem('pendingMessage');
      if (pendingMessage) {
        localStorage.removeItem('pendingMessage');
        // Send the pending message after a brief delay to ensure UI is ready
        setTimeout(() => {
          sendChatMessage(pendingMessage);
        }, 100);
      }
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to create new chat session', variant: 'destructive' });
    }
  });

  const deleteSessionMutation = useMutation({
    mutationFn: async (sessionId: number) => {
      return await apiRequest('DELETE', `/api/smart-chat/sessions/${sessionId}`);
    },
    onSuccess: (_, deletedSessionId) => {
      queryClient.invalidateQueries({ queryKey: ['/api/smart-chat/sessions'] });
      if (selectedSession === deletedSessionId) {
        setSelectedSession(null);
      }
      toast({ title: 'Chat deleted', description: 'The chat session has been removed' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to delete chat session', variant: 'destructive' });
    }
  });

  const updateSessionMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: number; updates: any }) => {
      const response = await fetch(`/api/smart-chat/sessions/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates)
      });
      if (!response.ok) throw new Error('Failed to update session');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/smart-chat/sessions'] });
      setEditingSessionId(null);
      toast({ title: 'Chat renamed', description: 'The chat title has been updated' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to update chat title', variant: 'destructive' });
    }
  });


  // Helper functions (copied from SmartAssistant)
  const handleNewChat = () => {
    createSessionMutation.mutate();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return format(date, 'HH:mm');
    } else if (diffInHours < 24 * 7) {
      return format(date, 'EEE');
    } else {
      return format(date, 'MMM dd');
    }
  };

  // Match ai-smart grouping logic exactly
  const groupSessionsByDate = (sessions: ChatSession[] | null | undefined): Record<string, ChatSession[]> => {
    if (!sessions || !Array.isArray(sessions)) {
      return {};
    }
    
    return sessions.reduce((acc: Record<string, ChatSession[]>, session: ChatSession) => {
      const date = new Date(session.createdAt);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      let category: string;
      if (date.toDateString() === today.toDateString()) {
        category = 'Today';
      } else if (date.toDateString() === yesterday.toDateString()) {
        category = 'Yesterday';
      } else if (date.getTime() > today.getTime() - 7 * 24 * 60 * 60 * 1000) {
        category = 'This Week';
      } else if (date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear()) {
        category = 'This Month';
      } else {
        category = 'Older';
      }
      
      if (!acc[category]) {
        acc[category] = [];
      }
      
      acc[category].push(session);
      return acc;
    }, {});
  };



  const groupedSessions = groupSessionsByDate(sessions || []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current && sessionData?.messages) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [sessionData?.messages]);

  // Send message function (matching SmartAssistant implementation)
  const handleSendMessage = async () => {
    if (!messageInput.trim()) return;
    
    if (!selectedSession) {
      // Create a new session when user sends first message from welcome screen
      createSessionMutation.mutate();
      // Store message to send after session creation
      localStorage.setItem('pendingMessage', messageInput);
      return;
    }
    
    // Send the chat message using the working approach
    sendChatMessage(messageInput);
  };

  const sendChatMessage = async (content: string) => {
    if (!selectedSession) return;
    
    // Set waiting state
    setIsWaitingForResponse(true);
    
    // Clear input
    setMessageInput('');
    
    // Create a temporary user message to show immediately
    const tempUserMessage = {
      id: -999,
      sessionId: selectedSession,
      role: 'user',
      content: content,
      createdAt: new Date().toISOString()
    };
    
    // Get current session data
    const currentData = queryClient.getQueryData(['/api/smart-chat/sessions', selectedSession]) as any;
    
    // Show user message immediately
    if (currentData) {
      const newMessages = [...currentData.messages, tempUserMessage];
      queryClient.setQueryData(
        ['/api/smart-chat/sessions', selectedSession],
        { ...currentData, messages: newMessages }
      );
    } else {
      // If this is a brand new session and we don't have data yet
      const currentSessions = queryClient.getQueryData<ChatSession[]>(['/api/smart-chat/sessions']) || [];
      const sessionInfo = currentSessions.find(s => s.id === selectedSession);
      
      if (sessionInfo) {
        // Create an initial session data structure
        const initialSessionData = {
          session: sessionInfo,
          messages: [tempUserMessage]
        };
        
        // Add it to the cache
        queryClient.setQueryData(
          ['/api/smart-chat/sessions', selectedSession],
          initialSessionData
        );
      }
    }
    
    try {
      // Send the message to the server
      const response = await fetch(`/api/smart-chat/sessions/${selectedSession}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to send message (${response.status})`);
      }
      
      // Get response with real messages
      const data = await response.json();
      
      // Get current messages from cache
      const currentSessionData = queryClient.getQueryData(['/api/smart-chat/sessions', selectedSession]) as any;
      
      if (currentSessionData) {
        // Safely get the user message from response or create a placeholder if not found
        const userMessage = data.userMessage || {
          id: Date.now(),
          sessionId: selectedSession,
          role: 'user',
          content: content,
          createdAt: new Date().toISOString()
        };
        
        // Replace temp message with real one
        const messagesWithoutTemp = currentSessionData.messages.filter((msg: any) => msg.id !== -999);
        const messagesWithUser = [...messagesWithoutTemp, userMessage];
        
        // Update cache with real user message
        queryClient.setQueryData(
          ['/api/smart-chat/sessions', selectedSession],
          { ...currentSessionData, messages: messagesWithUser }
        );
        
        // Handle assistant message
        if (!data.assistantMessage && data.aiMessage) {
          data.assistantMessage = data.aiMessage;
        }
        
        if (data.assistantMessage) {
          // Add assistant message to cache
          const finalMessages = [...messagesWithUser, data.assistantMessage];
          queryClient.setQueryData(
            ['/api/smart-chat/sessions', selectedSession],
            { ...currentSessionData, messages: finalMessages }
          );
        }
      }
      
      // Update the sessions list with latest update time
      const currentSessions = queryClient.getQueryData<ChatSession[]>(['/api/smart-chat/sessions']) || [];
      const updatedSessions = currentSessions.map(session => 
        session.id === selectedSession 
          ? { ...session, updatedAt: new Date().toISOString() }
          : session
      );
      queryClient.setQueryData(['/api/smart-chat/sessions'], updatedSessions);
      try {
        // Ensure we refetch the full session to pick up any DB-transformed metadata
        queryClient.invalidateQueries({ queryKey: ['/api/smart-chat/sessions', selectedSession] });
      } catch {}
      
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: 'Error sending message',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
      
      // Remove temp message on error
      if (currentData) {
        const messagesWithoutTemp = currentData.messages.filter((msg: any) => msg.id !== -999);
        queryClient.setQueryData(
          ['/api/smart-chat/sessions', selectedSession],
          { ...currentData, messages: messagesWithoutTemp }
        );
      }
    } finally {
      setIsWaitingForResponse(false);
    }
  };

  // Reset conversation when dialog changes
  useEffect(() => {
    if (selectedDialog) {
      setSpecializedInput('');
      setConversationHistory([]);
      setIsProcessing(false);
    }
  }, [selectedDialog]);

  const handleSpecializedTask = async (type: string) => {
    if (!specializedInput.trim()) return;
    
    setIsProcessing(true);
    const userMessage = specializedInput.trim();
    setSpecializedInput('');
    
    // Add user message to conversation history
    const newHistory = [...conversationHistory, { role: 'user', content: userMessage }];
    setConversationHistory(newHistory);
    
    try {
      // Create enhanced context-aware system prompts
      let systemPrompt = '';
      let contextualPrompt = '';
      
      switch (type) {
        case 'marketing':
          systemPrompt = `You are AIVIA Marketing Assistant, an expert in travel industry marketing. You help travel agents and agencies with:

CORE FUNCTIONS:
• Campaign creation and execution strategy
• Content writing (social media, emails, newsletters, blogs)
• Lead generation and customer acquisition tactics
• Brand messaging and positioning
• Market analysis and competitor research
• Email marketing automation and sequences
• Social media content planning and scheduling

INTERACTION STYLE:
• Provide specific, actionable recommendations
• Include implementation steps and timelines
• Suggest metrics to track success
• Reference travel industry best practices
• Offer creative content ideas and templates
• Be conversational but professional

When users ask for content creation, provide complete drafts. When they ask for strategy, include specific tactics and next steps.`;

          contextualPrompt = `Current conversation context: This is a specialized marketing assistant session. The user is seeking help with travel marketing tasks. Previous conversation: ${conversationHistory.length > 0 ? conversationHistory.map(m => `${m.role}: ${m.content}`).join('\n') : 'None'}`;
          break;
          
        case 'sales':
          systemPrompt = `You are AIVIA Sales Assistant, specialized in travel sales processes and client relationship management. You excel at:

CORE FUNCTIONS:
• Pipeline management and lead qualification
• Proposal and quote generation for travel packages
• Client communication scripts and follow-up sequences
• Sales objection handling and closing techniques
• Customer relationship management strategies
• Travel package customization and upselling
• Pricing strategies and competitive positioning
• Sales performance tracking and reporting

INTERACTION STYLE:
• Provide specific scripts and email templates
• Include pricing guidance and negotiation tactics
• Suggest client qualification questions
• Offer objection handling responses
• Create detailed follow-up sequences
• Focus on conversion optimization

When creating proposals, include package details, pricing rationale, and compelling value propositions.`;

          contextualPrompt = `Current conversation context: This is a specialized sales assistant session. The user needs help with travel sales processes. Previous conversation: ${conversationHistory.length > 0 ? conversationHistory.map(m => `${m.role}: ${m.content}`).join('\n') : 'None'}`;
          break;
          
        case 'finances':
          systemPrompt = `You are AIVIA Finance Assistant, an expert in travel business financial management and analysis. You specialize in:

CORE FUNCTIONS:
• Financial reporting and performance analysis
• Revenue tracking and forecasting models
• Expense management and cost optimization
• Budget planning and resource allocation
• Profit margin analysis by service/destination
• Cash flow management and seasonal planning
• Tax planning and compliance guidance
• Financial KPIs and business metrics tracking

INTERACTION STYLE:
• Provide specific formulas and calculation methods
• Include actionable cost-saving recommendations
• Suggest financial planning templates and tools
• Offer industry benchmarks and comparisons
• Create clear financial summaries and reports
• Focus on profit optimization strategies

When analyzing finances, include specific numbers, percentages, and actionable insights.`;

          contextualPrompt = `Current conversation context: This is a specialized finance assistant session. The user requires help with travel business financial matters. Previous conversation: ${conversationHistory.length > 0 ? conversationHistory.map(m => `${m.role}: ${m.content}`).join('\n') : 'None'}`;
          break;
      }

      const messages = [
        { role: 'system', content: systemPrompt },
        { role: 'system', content: contextualPrompt },
        ...newHistory
      ];

      const response = await fetch('/api/chat/message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          messages,
          provider: DEFAULT_AI_PROVIDER,
          model: DEFAULT_AI_MODEL,
        })
      });

      if (!response.ok) throw new Error('Failed to get AI response');
      
      const data = await response.json();
      
      // Add AI response to conversation history
      const assistantMessage = { role: 'assistant', content: data.message };
      setConversationHistory([...newHistory, assistantMessage]);
      
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to get AI assistance',
        variant: 'destructive'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const getQuickActions = (type: string) => {
    switch (type) {
      case 'marketing':
        return [
          { text: 'Create a compelling social media post for our European river cruise packages targeting empty nesters', icon: Share2 },
          { text: 'Write a 5-email drip campaign sequence for spring travel deals with booking incentives', icon: Mail },
          { text: 'Generate 10 engaging travel blog post ideas focused on experiential luxury travel', icon: Lightbulb },
          { text: 'Develop a complete marketing strategy for our new wellness retreat packages', icon: Target },
          { text: 'Draft a monthly newsletter featuring client success stories and new destinations', icon: FileText },
          { text: 'Create Instagram captions for Mediterranean cruise photos with engagement hooks', icon: Camera },
          { text: 'Write Google Ads copy for family vacation packages to Greece and Italy', icon: Globe }
        ];
      case 'sales':
        return [
          { text: 'Create a detailed proposal for a 10-day family trip to Italy including Rome, Florence, and Tuscany', icon: FileText },
          { text: 'Generate personalized follow-up email templates for different types of travel inquiries', icon: Mail },
          { text: 'Write a consultative sales script for selling honeymoon packages to Bali and Maldives', icon: MessageSquare },
          { text: 'Develop a pricing strategy and value proposition for group bookings of 8+ travelers', icon: Users },
          { text: 'Draft a compelling client presentation for corporate retreat planning services', icon: Building },
          { text: 'Create objection handling responses for price-sensitive leisure travel clients', icon: Target },
          { text: 'Write a proposal template for luxury multi-generational family vacations', icon: Calendar }
        ];
      case 'finances':
        return [
          { text: 'Analyze Q4 revenue performance by destination and identify top-performing packages', icon: PieChart },
          { text: 'Create a detailed marketing budget allocation strategy for the upcoming travel season', icon: TrendingUp },
          { text: 'Generate a profit margin analysis comparing river cruises vs. ocean cruises', icon: DollarSign },
          { text: 'Develop a cash flow forecast considering seasonal booking patterns and payment schedules', icon: Calendar },
          { text: 'Calculate ROI and performance metrics for our current advertising campaigns across channels', icon: Target },
          { text: 'Create a cost analysis for adding new destination partnerships vs. existing suppliers', icon: PieChart },
          { text: 'Build a financial dashboard showing key metrics: bookings, revenue, and commission rates', icon: FileText }
        ];
      default:
        return [];
    }
  };

  const renderSpecializedAiAssistant = (type: string) => {
    return (
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold capitalize">{type} Assistant</h2>
              <p className="text-sm text-gray-600">
                {type === 'marketing' && 'Get help with campaigns, content creation, and lead generation'}
                {type === 'sales' && 'Assistance with proposals, client communication, and sales strategy'}
                {type === 'finances' && 'Support with financial analysis, reporting, and budget planning'}
              </p>
            </div>
            {type === 'marketing' && (
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Marketing Preferences
              </Button>
            )}
          </div>
        </div>
        {/* Recommended Actions */}
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-sm font-medium mb-4">Recommended Actions</h3>
          <div className="grid grid-cols-2 gap-3">
            {getQuickActions(type).slice(0, 6).map((action, index) => {
              const actionData = typeof action === 'object' ? action : { text: action, icon: Megaphone };
              const ActionIcon = actionData.icon || Megaphone;
              return (
                <Card
                  key={index}
                  className="cursor-pointer hover:shadow-md transition-all duration-200 border-2 hover:border-orange-200 bg-gradient-to-r from-gray-50 to-white"
                  onClick={() => setSpecializedInput(actionData.text)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        type === 'marketing' ? 'bg-pink-100 text-pink-600' :
                        type === 'sales' ? 'bg-green-100 text-green-600' :
                        'bg-blue-100 text-blue-600'
                      }`}>
                        <ActionIcon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 line-clamp-3 text-[16px]">
                          {actionData.text}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
        {/* Chat Area */}
        <div className="flex-1 flex flex-col">
          {/* Conversation History */}
          {conversationHistory.length > 0 && (
            <div className="flex-1 p-4 overflow-y-auto">
              <div className="space-y-4">
                {conversationHistory.map((msg, index) => (
                  <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] rounded-lg p-3 ${
                      msg.role === 'user' 
                        ? 'bg-orange-500 text-white' 
                        : 'bg-gray-100 text-gray-900'
                    }`}>
                      <div className="whitespace-pre-wrap text-sm">
                        {msg.content}
                      </div>
                      {/* Action Buttons for AI responses */}
                      {msg.role === 'assistant' && index === conversationHistory.length - 1 && (
                        <div className="mt-3 pt-3 border-t border-gray-200 flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs"
                            onClick={() => setSpecializedInput('Can you expand on this with more specific details?')}
                          >
                            More Details
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs"
                            onClick={() => setSpecializedInput('Create a step-by-step implementation plan for this')}
                          >
                            Implementation Plan
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs"
                            onClick={() => setSpecializedInput('What are the potential challenges and how can we overcome them?')}
                          >
                            Challenges & Solutions
                          </Button>
                          {type === 'marketing' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs"
                              onClick={() => setSpecializedInput('Create 3 different variations of this content for A/B testing')}
                            >
                              A/B Test Versions
                            </Button>
                          )}
                          {type === 'sales' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs"
                              onClick={() => setSpecializedInput('Create a follow-up sequence for this proposal')}
                            >
                              Follow-up Sequence
                            </Button>
                          )}
                          {type === 'finances' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs"
                              onClick={() => setSpecializedInput('Show me how to track these metrics in a dashboard format')}
                            >
                              Dashboard View
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Processing State */}
          {isProcessing && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin text-orange-500 mx-auto mb-2" />
                <p className="text-sm text-gray-500">AIVIA is working on your {type} task...</p>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!conversationHistory.length && !isProcessing && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  {type === 'marketing' && <Megaphone className="h-6 w-6" />}
                  {type === 'sales' && <TrendingUp className="h-6 w-6" />}
                  {type === 'finances' && <DollarSign className="h-6 w-6" />}
                </div>
                <p className="text-sm">Ask me anything about {type}</p>
                <p className="text-xs mt-1">Or try one of the quick actions above</p>
              </div>
            </div>
          )}

          {/* Input Area */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex gap-2">
              <Textarea
                placeholder={`Ask AIVIA for ${type} help...`}
                value={specializedInput}
                onChange={(e) => setSpecializedInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSpecializedTask(type);
                  }
                }}
                className="flex-1 min-h-[44px] max-h-[120px] resize-none"
                disabled={isProcessing}
              />
              <Button
                onClick={() => handleSpecializedTask(type)}
                disabled={!specializedInput.trim() || isProcessing}
                className="bg-orange-500 hover:bg-orange-600 text-white px-4"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Left Sidebar - Chat Sessions */}
      <div className="w-80 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <Button 
            onClick={handleNewChat}
            disabled={createSessionMutation.isPending}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-lg h-11 font-medium"
          >
            {createSessionMutation.isPending ? 'Creating...' : 'New Workspace'}
          </Button>
        </div>

        {/* Chat Sessions List */}
        <div className="flex-1 overflow-y-auto p-4">
          {!isLoadingSessions && sessions?.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm">No chat sessions yet</p>
              <p className="text-xs mt-1">Start a new chat to get help from AIVIA Smart</p>
            </div>
          ) : (
            <div className="space-y-1">
              {Object.entries(groupedSessions).map(([category, categorySessions]) => (
                <div key={category} className="mb-4">
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 px-2">
                    {category}
                  </h3>
                  <div className="space-y-1">
                    {categorySessions.map((session) => (
                      <div
                        key={session.id}
                        className={`group relative px-3 py-2.5 rounded-lg cursor-pointer transition-all ${
                          selectedSession === session.id 
                            ? 'bg-orange-50 border-l-2 border-orange-500' 
                            : 'hover:bg-gray-50'
                        }`}
                        onClick={() => setSelectedSession(session.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            {editingSessionId === session.id ? (
                              <Input
                                value={editSessionTitle}
                                onChange={(e) => setEditSessionTitle(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    if (editSessionTitle.trim()) {
                                      updateSessionMutation.mutate({
                                        id: session.id,
                                        updates: { title: editSessionTitle }
                                      });
                                    } else {
                                      setEditingSessionId(null);
                                    }
                                  } else if (e.key === 'Escape') {
                                    setEditingSessionId(null);
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                                className="h-6 text-sm border-0 p-0 focus:ring-0"
                              />
                            ) : (
                              <p className={`text-sm truncate ${
                                selectedSession === session.id ? 'text-orange-700 font-medium' : 'text-gray-700'
                              }`}>
                                {session.title}
                              </p>
                            )}
                          </div>
                          
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button 
                                variant="ghost" 
                                size="icon" aria-label={`Actions for ${session.title}`} 
                                className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <MoreVertical className="h-3 w-3 text-gray-400" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={(e) => {
                                e.stopPropagation();
                                setEditingSessionId(session.id);
                                setEditSessionTitle(session.title);
                              }}>
                                <Edit className="mr-2 h-4 w-4" />
                                Rename
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="text-red-600"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteSessionMutation.mutate(session.id);
                                }}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right Side - Split between Chat and Workspace Cards */}
      <div className="flex-1 flex">
        {/* Chat Area */}
        <div className="flex-1 flex flex-col bg-white dark:bg-gray-800">
      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto p-6">
        <Alert className="mb-4 border-dashed border-orange-200 bg-orange-50/60">
          <AlertTitle className="text-orange-900">Workspace is legacy for SmartChat replay</AlertTitle>
          <AlertDescription className="text-sm text-orange-900/80">
            The rating controls and orchestration replay panel now live on the Threads Canvas experience (<Link to="/threads" className="underline">/threads</Link>) and are gated by the <code className="rounded bg-white/70 px-1 py-0.5 text-xs">threadReplayFeedback</code> flag. Continue development, QA, and feedback collection there; this page is kept for historical experiments only.
          </AlertDescription>
        </Alert>
        {selectedSession ? (
          <>
            {isLoadingMessages ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
                  </div>
                ) : (
                  <div className="max-w-4xl mx-auto py-6 px-4">
                    {sessionData?.messages?.map((message: ChatMessage) => {
                      return (
                      <div
                        key={message.id}
                        className={`mb-6 ${
                          message.role === 'assistant' ? 'bg-white/80 dark:bg-gray-700/80' : 'bg-white/60 dark:bg-gray-600/60'
                        } rounded-lg p-4`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 ${
                            message.role === 'assistant' 
                              ? '' 
                              : ''
                          }`}>
                            {message.role === 'assistant' ? (
                              <img src="/assets/aivia-icon-new.png" alt="AIVIA" className="h-5 w-5" />
                            ) : (
                              user?.email ? (
                                <UserAvatar 
                                  email={user.email} 
                                  name={user.displayName} 
                                  size={32} 
                                  className=""
                                />
                              ) : (
                                <User className="h-4 w-4 text-gray-600" />
                              )
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium text-gray-900">
                                {message.role === 'assistant' ? 'AIVIA' : 'You'}
                              </span>
                              <span className="text-xs text-gray-500">
                                {format(new Date(message.createdAt), 'h:mm a')}
                              </span>
                            </div>
                            <div className="prose max-w-none">
                              <MessageContent content={message.content} />
                              {message.role === 'assistant' && (
                                <>
                                  {(message as any)?.metadata?.unsplash?.images?.length > 0 && (
                                    <UnsplashGrid meta={(message as any).metadata} />
                                  )}
                                </>
                              )}
                            </div>
                            {message.metadata && getOrchestratorStatusBadge(message.metadata)}
                          </div>
                        </div>
                      </div>
                    );
                    })}
                    {isWaitingForResponse && (
                      <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                            <img src="/assets/aivia-icon-new.png" alt="AIVIA" className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium text-gray-900">AIVIA</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Loader2 className="h-4 w-4 animate-spin text-orange-500" />
                              <span className="text-sm text-gray-500">Thinking...</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <div className="text-center">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium mb-2">Welcome to AIVIA Workspace</p>
                  <p className="text-sm">Start a new chat or select an existing one to begin</p>
                </div>
              </div>
            )}
          </div>

          {/* Message Input - Always Visible */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex gap-2">
              <Textarea
                placeholder="Ask AIVIA anything..."
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                className="flex-1 min-h-[44px] max-h-[120px] resize-none"
                disabled={isWaitingForResponse}
              />
              <Button
                onClick={handleSendMessage}
                disabled={!messageInput.trim() || isWaitingForResponse}
                className="bg-orange-500 hover:bg-orange-600 text-white px-4"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Workspace Cards Area */}
        <div className="w-96 bg-gray-50 border-l border-gray-200 p-6 overflow-y-auto">
          <div className="space-y-6">
            {/* Row 1: To Do and To Approve */}
            <div className="space-y-4">
              {/* To Do Card */}
              <Card className={todoCollapsed ? "h-auto" : "h-64"}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
                  <CardTitle 
                    className="text-sm font-semibold flex items-center gap-2 my-0 cursor-pointer hover:bg-gray-50 rounded flex-1 p-2 -m-2"
                    onClick={() => setTodoCollapsed(!todoCollapsed)}
                  >
                    <CheckSquare className="h-4 w-4 text-emerald-500" />
                    To Do
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">{todoItems.length}</Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 hover:bg-gray-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMaximizedCard('todo');
                      }}
                    >
                      <Maximize2 className="h-3 w-3 text-gray-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 hover:bg-gray-100"
                      onClick={() => setTodoCollapsed(!todoCollapsed)}
                    >
                      {todoCollapsed ? (
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronUp className="h-4 w-4 text-gray-500" />
                      )}
                    </Button>
                  </div>
                </CardHeader>
                {!todoCollapsed && (
                  <CardContent className="text-xs">
                    <ScrollArea className="h-40">
                      <div className="space-y-2 pr-4">
                        {todoItems.map((item) => (
                          <div key={item.id} className="flex items-center justify-between p-2 rounded border hover:bg-gray-50">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{item.title}</p>
                              <p className="text-gray-500">{item.dueDate}</p>
                            </div>
                            <Badge className={`text-xs ${getPriorityColor(item.priority)}`}>
                              {item.priority}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                )}
              </Card>

              {/* To Approve Card */}
              <Card className={approvalCollapsed ? "h-auto" : "h-64"}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
                  <CardTitle 
                    className="text-sm font-semibold flex items-center gap-2 my-0 cursor-pointer hover:bg-gray-50 rounded flex-1 p-2 -m-2"
                    onClick={() => setApprovalCollapsed(!approvalCollapsed)}
                  >
                    <FileCheck className="h-4 w-4 text-orange-500" />
                    To Approve
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">{approvalItems.length}</Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 hover:bg-gray-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMaximizedCard('approve');
                      }}
                    >
                      <Maximize2 className="h-3 w-3 text-gray-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 hover:bg-gray-100"
                      onClick={() => setApprovalCollapsed(!approvalCollapsed)}
                    >
                      {approvalCollapsed ? (
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronUp className="h-4 w-4 text-gray-500" />
                      )}
                    </Button>
                  </div>
                </CardHeader>
                {!approvalCollapsed && (
                  <CardContent className="text-xs">
                    <ScrollArea className="h-40">
                      <div className="space-y-2 pr-4">
                        {approvalItems.map((item) => (
                          <div key={item.id} className="p-2 rounded border hover:bg-gray-50">
                            <p className="font-medium">{item.title}</p>
                            <p className="text-gray-500 capitalize mb-2">{item.type}</p>
                            <div className="flex gap-1">
                              <Button size="sm" variant="outline" className="h-6 text-xs">Deny</Button>
                              <Button size="sm" className="h-6 text-xs bg-emerald-600 hover:bg-emerald-700">Approve</Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                )}
              </Card>
            </div>

            {/* Row 2: Business Function Cards */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">Business Functions</h3>
              <div className="space-y-2">
                {/* Marketing Card */}
                <Card 
                  className="h-20 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => setSelectedDialog("marketing")}
                >
                  <CardContent className="flex items-center p-3">
                    <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center mr-3">
                      <Megaphone className="h-4 w-4 text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">Marketing</p>
                      <p className="text-xs text-gray-500">Campaign management</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Sales Card */}
                <Card 
                  className="h-20 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => setSelectedDialog("sales")}
                >
                  <CardContent className="flex items-center p-3">
                    <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-3">
                      <TrendingUp className="h-4 w-4 text-orange-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">Sales</p>
                      <p className="text-xs text-gray-500">Pipeline management</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Finances Card */}
                <Card 
                  className="h-20 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => setSelectedDialog("finances")}
                >
                  <CardContent className="flex items-center p-3">
                    <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center mr-3">
                      <DollarSign className="h-4 w-4 text-slate-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">Finances</p>
                      <p className="text-xs text-gray-500">Revenue tracking</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Row 3: Quick Actions */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">Quick Actions</h3>
              <div className="grid grid-cols-2 gap-2">
                {quickActions.slice(0, 4).map((action, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    className="h-16 flex flex-col items-center justify-center gap-1 text-xs"
                    onClick={action.action}
                  >
                    <action.icon className="h-4 w-4" />
                    <span>{action.title}</span>
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Maximized Card Dialog */}
      <Dialog 
        open={maximizedCard !== null} 
        onOpenChange={() => setMaximizedCard(null)}
      >
        <DialogContent className="max-w-[90vw] max-h-[90vh] w-[90vw] h-[90vh] p-0 flex flex-col">
          {maximizedCard === 'todo' && (
            <>
              <DialogHeader className="flex-row items-center justify-between p-6 border-b flex-shrink-0 space-y-0">
                <div className="flex items-center gap-3">
                  <CheckSquare className="h-5 w-5 text-emerald-500" />
                  <DialogTitle className="text-xl font-semibold">To Do Items</DialogTitle>
                  <Badge variant="secondary">{todoItems.length} items</Badge>
                </div>
                <DialogDescription className="sr-only">
                  View and manage all to-do items for your workspace
                </DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto min-h-0">
                <div className="p-6 space-y-4">
                  {todoItems.map((item) => (
                    <Card key={item.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                            <p className="text-gray-600 mb-3">{item.description}</p>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span>Due: {item.dueDate}</span>
                              <span>Assigned to: {item.assignedTo}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className={`${getPriorityColor(item.priority)}`}>
                              {item.priority}
                            </Badge>
                            <Button size="sm" variant="outline">
                              Mark Complete
                            </Button>
                            <Button size="sm">
                              View Details
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </>
          )}
            
          {maximizedCard === 'approve' && (
            <>
              <DialogHeader className="flex-row items-center justify-between p-6 border-b flex-shrink-0 space-y-0">
                <div className="flex items-center gap-3">
                  <FileCheck className="h-5 w-5 text-orange-500" />
                  <DialogTitle className="text-xl font-semibold">Items to Approve</DialogTitle>
                  <Badge variant="secondary">{approvalItems.length} items</Badge>
                </div>
                <DialogDescription className="sr-only">
                  Review and approve pending items that require your authorization
                </DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto min-h-0">
                <div className="p-6 space-y-4">
                  {approvalItems.map((item) => (
                    <Card key={item.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                            <p className="text-gray-600 mb-2 capitalize">{item.type}</p>
                            <p className="text-gray-600 mb-3">{item.description}</p>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span>Submitted: {item.submittedDate}</span>
                              <span>By: {item.submittedBy}</span>
                              {item.amount && <span>Amount: {item.amount}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700">
                              Deny
                            </Button>
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                              Approve
                            </Button>
                            <Button size="sm" variant="ghost">
                              View Details
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Specialized AI Assistant Dialogs */}
      <Dialog 
        open={selectedDialog !== null} 
        onOpenChange={() => setSelectedDialog(null)}
      >
        <DialogContent className="max-w-[90vw] max-h-[90vh] w-[90vw] h-[90vh] p-0">
          <DialogHeader className="sr-only">
            <DialogTitle>
              {selectedDialog === 'marketing' && 'Marketing Assistant'}
              {selectedDialog === 'sales' && 'Sales Assistant'}
              {selectedDialog === 'finances' && 'Finance Assistant'}
            </DialogTitle>
            <DialogDescription>
              {selectedDialog === 'marketing' && 'Get help with campaigns, content creation, and lead generation'}
              {selectedDialog === 'sales' && 'Assistance with proposals, client communication, and sales strategy'}
              {selectedDialog === 'finances' && 'Support with financial analysis, reporting, and budget planning'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 p-6 overflow-hidden">
            {selectedDialog && renderSpecializedAiAssistant(selectedDialog)}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
